import { CONTACT_FILTERS, PIPELINE_FILTER_KEYS, RANKING_FILTER_KEYS, RANK_TABS, decodeFilterValues, encodeFilterValues, uuidSchema, type ContactFilter, type PipelineFilter, type PipelineLayout, type RankTab, type RankingFilterKey } from '@nest-recruit/shared';

// Everything the pipeline shows is in its address (06-qol §6): the filters as the API
// reads them, the page, the layout, the saved view in use, and — from N-73 — the bulk
// action whose report to open. Copying the link is sharing the view (S16 QA test 5).

/** ADR 0049 (F23.11): RNK-01's filters, each a list of values joined by "|". */
export type RankingFilterSearch = { readonly [K in RankingFilterKey]?: string };

export interface PipelineSearch extends PipelineFilter, RankingFilterSearch {
  readonly page?: number;
  readonly layout?: PipelineLayout;
  readonly view?: string;
  readonly action?: string;
  /** R4: the import the Import stage shows — N-86 opens IMP-02 at it (§L22). */
  readonly import?: string;
  // R5 — RNK-01 keeps its whole view in the address too, so a link or a saved view reopens it.
  /** The ranking tab; N-87 opens `top_fit`. */
  readonly tab?: RankTab;
  /** F23.04: the criterion keys being focused on, comma-separated. */
  readonly focus?: string;
  /** `score` or a criterion key, then `:asc` or `:desc`. */
  readonly rsort?: string;
  readonly rlayout?: 'list';
  /** RNK-02: the candidate whose "Why this score" is open. */
  readonly app?: string;
  /**
   * ADR 0050 (F23.15): the Jev ask on screen, and whether the list is in Jev's order. `rorder` also names the other two orders (ADR 0062):
   * the ideal candidate (the job's main ranking, so the default) and the screening criteria's score (the lens, only with that module on).
   * Saved links with `icp` or `score` still open.
   */
  readonly rask?: string;
  readonly rorder?: 'jev' | 'icp' | 'score';
  /**
   * Workflow review D5: the Automated ranking tab, ICP match (the default) or Jev search. A link from before the tabs has none:
   * `rorder=jev` or an ask (`rask`) opens Jev search, anything else ICP match (`rankingPaneOf`).
   */
  readonly rtab?: RankingPane;
  /** ADR 0068: the filters on the profile's lines Jev reads, "lineId:rating" joined by "|". */
  readonly rline?: string;
  /** D6 (ADR 0080), evening review item 8: ④ Review's "Reviewed by" filter, a reviewer's user id; absent means All. */
  readonly rwho?: string;
  // R7 — stage ⑥ Contact keeps its view in the address too.
  /** Tracker (the default), the planner table, or its calendar (DECISIONS-LOG "Start R7" Q2). */
  readonly cview?: ContactView;
  /** CT-4's filter (O34). */
  readonly cfilter?: ContactFilter;
  /** PLN-01's side panel: the interview whose history is open. */
  readonly iv?: string;
  /** PLN-02: the Sunday the week starts on, or the day shown, `YYYY-MM-DD`. */
  readonly cweek?: string;
  readonly cspan?: 'day';
  // ADR 0079 — PHS-01, the phone Screening step: the round on screen (1 is the default) and who it shows.
  readonly sround?: number;
  /** Waiting is the default. */
  readonly sshow?: PhoneScreenShow;
}

export const PHONE_SCREEN_SHOWS = ['waiting', 'passed', 'out'] as const;
export type PhoneScreenShow = (typeof PHONE_SCREEN_SHOWS)[number];

export const RANKING_PANES = ['icp', 'jev'] as const;
export type RankingPane = (typeof RANKING_PANES)[number];

/** Workflow review D5: the tab Automated ranking opens on. Jev search only while Ask Jev (`jev`) is on (#13). */
export function rankingPaneOf(search: Pick<PipelineSearch, 'rtab' | 'rorder' | 'rask'>, isJevOn: boolean): RankingPane {
  if (!isJevOn) return 'icp';
  if (search.rtab) return search.rtab;
  return search.rorder === 'jev' || search.rask !== undefined ? 'jev' : 'icp';
}

export const CONTACT_VIEWS = ['tracker', 'planner', 'calendar'] as const;
export type ContactView = (typeof CONTACT_VIEWS)[number];
/** ④ Review's "Reviewed by" filter: a user id (evening review item 8). */
const REVIEWER_PATTERN = /^[\w-]{1,64}$/;
const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const FOCUS_PATTERN = /^[0-9a-f-]{36}(,[0-9a-f-]{36})*$/i;
const RANKING_FILTER_MAX = 1000;
const RANK_SORT_PATTERN = /^(score|[0-9a-f-]{36}):(asc|desc)$/i;

export function validatePipelineSearch(search: Record<string, unknown>): PipelineSearch {
  const next: Record<string, string | number> = {};
  for (const key of PIPELINE_FILTER_KEYS) {
    const value = search[key];
    if (typeof value === 'string' && value !== '') next[key] = value;
    // The router parses `70` as a number; the filter is strings throughout.
    else if (typeof value === 'number') next[key] = String(value);
  }
  const page = Number(search.page);
  if (Number.isInteger(page) && page > 1) next.page = page;
  if (search.layout === 'board') next.layout = 'board';
  if (typeof search.view === 'string' && uuidSchema.safeParse(search.view).success) next.view = search.view;
  if (typeof search.action === 'string' && uuidSchema.safeParse(search.action).success) next.action = search.action;
  if (typeof search.import === 'string' && uuidSchema.safeParse(search.import).success) next.import = search.import;
  const tab = RANK_TABS.find((candidate) => candidate === search.tab);
  if (tab) next.tab = tab;
  if (typeof search.focus === 'string' && FOCUS_PATTERN.test(search.focus)) next.focus = search.focus;
  if (typeof search.rsort === 'string' && RANK_SORT_PATTERN.test(search.rsort)) next.rsort = search.rsort;
  if (search.rlayout === 'list') next.rlayout = 'list';
  if (typeof search.app === 'string' && uuidSchema.safeParse(search.app).success) next.app = search.app;
  if (typeof search.rask === 'string' && uuidSchema.safeParse(search.rask).success) next.rask = search.rask;
  if (search.rorder === 'jev' || search.rorder === 'icp' || search.rorder === 'score') next.rorder = search.rorder;
  const rtab = RANKING_PANES.find((candidate) => candidate === search.rtab);
  if (rtab) next.rtab = rtab;
  for (const key of RANKING_FILTER_KEYS) {
    const value = search[key];
    const kept = typeof value === 'string' && value.length <= RANKING_FILTER_MAX ? encodeFilterValues(decodeFilterValues(value)) : undefined;
    if (kept) next[key] = kept;
  }
  const rline = typeof search.rline === 'string' && search.rline.length <= RANKING_FILTER_MAX ? encodeFilterValues(decodeFilterValues(search.rline)) : undefined;
  if (rline) next.rline = rline;
  // A reviewer's id; only ever compared, never sent, so any id-shaped text is kept.
  if (typeof search.rwho === 'string' && REVIEWER_PATTERN.test(search.rwho) && search.rwho !== 'all') next.rwho = search.rwho;
  const cview = CONTACT_VIEWS.find((candidate) => candidate === search.cview && candidate !== 'tracker');
  if (cview) next.cview = cview;
  const cfilter = CONTACT_FILTERS.find((candidate) => candidate === search.cfilter && candidate !== 'all');
  if (cfilter) next.cfilter = cfilter;
  if (typeof search.iv === 'string' && uuidSchema.safeParse(search.iv).success) next.iv = search.iv;
  if (typeof search.cweek === 'string' && DAY_PATTERN.test(search.cweek)) next.cweek = search.cweek;
  if (search.cspan === 'day') next.cspan = 'day';
  const sround = Number(search.sround);
  if (Number.isInteger(sround) && sround > 1) next.sround = sround;
  if (search.sshow === 'passed' || search.sshow === 'out') next.sshow = search.sshow;
  return next;
}

/** The filter half — what the API and a saved view take. */
export function filterOf(search: PipelineSearch): PipelineFilter {
  const filter: Record<string, string> = {};
  for (const key of PIPELINE_FILTER_KEYS) {
    const value = search[key];
    if (value !== undefined && value !== '') filter[key] = value;
  }
  return filter;
}

/** Several filters changed at once — removing "Score 60–100" clears both ends in one step. */
export type FilterChanges = Partial<Record<keyof PipelineFilter, string | undefined>>;

/** The search with these filters changed, blanks removed; any change goes back to page 1. */
export function withFilters(search: PipelineSearch, changes: FilterChanges): PipelineSearch {
  const next: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(search)) {
    if (key === 'page' || key in changes || value === undefined) continue;
    next[key] = value;
  }
  for (const [key, value] of Object.entries(changes)) {
    if (value !== undefined && value !== '') next[key] = value;
  }
  return validatePipelineSearch(next);
}

export function listOf(value: string | undefined): readonly string[] {
  return value ? value.split(',').filter(Boolean) : [];
}
